"use client";

import { getSetupProgress, type SetupStep } from "@/app/actions/setup";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { CheckCircle2, ChevronRight, Circle } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

export function SetupProgress() {
  const [steps, setSteps] = useState<SetupStep[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSteps = async () => {
      const loadedSteps = await getSetupProgress();
      setSteps(loadedSteps);
      setIsLoading(false);
    };

    loadSteps();
  }, []);

  const completedSteps = steps.filter((step) => step.completed);
  const progress = (completedSteps.length / steps.length) * 100;

  if (isLoading) {
    return null;
  }

  if (completedSteps.length === steps.length) {
    return null;
  }

  return (
    <div className="sticky top-24" data-oid="zp__flv">
      <Card data-oid="hgrm7h5">
        <CardHeader data-oid="6ec1n3v">
          <CardTitle className="flex items-center gap-2 text-lg font-medium" data-oid="nm_yh08">
            <CheckCircle2 className="text-muted-foreground h-5 w-5" data-oid="0_f01hu" />
            Getting Started
          </CardTitle>
        </CardHeader>
        <CardContent data-oid="usrbh4r">
          <div className="space-y-6" data-oid="k0qxplg">
            <div className="space-y-2" data-oid="w3gmio2">
              <div className="flex items-center justify-between text-sm" data-oid="d3ilj0i">
                <span className="text-muted-foreground" data-oid="_6oat1t">
                  {completedSteps.length} of {steps.length} steps completed
                </span>
                <span className="font-medium" data-oid="j0bl:9p">
                  {Math.round(progress)}%
                </span>
              </div>
              <Progress value={progress} className="h-2" data-oid="2c78vos" />
            </div>
            <div className="space-y-2" data-oid="e06ymkn">
              {steps.map((step) => (
                <Link
                  key={step.id}
                  href={step.href}
                  className={cn(
                    "hover:bg-muted flex items-center space-x-4 rounded-lg border p-4 transition-colors",
                    step.completed && "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950"
                  )}
                  data-oid="va4m3-q"
                >
                  {step.completed ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" data-oid="y529ohe" />
                  ) : (
                    <Circle className="text-muted-foreground h-5 w-5" data-oid="cxt6xq3" />
                  )}
                  <div className="flex-1 space-y-1" data-oid="rz0ivmd">
                    <p className="leading-none font-medium" data-oid="y9axx4:">
                      {step.title}
                    </p>
                    <p className="text-muted-foreground text-sm" data-oid="nhcoj2w">
                      {step.description}
                    </p>
                  </div>
                  <ChevronRight className="text-muted-foreground h-5 w-5" data-oid="nf4-p4i" />
                </Link>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
